import {
	ExternalLink,
	FilePlus2,
	ListChecks,
	PencilLine,
	Plus,
	RotateCcw,
	X,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import {
	btn,
	ConfirmDialog,
	Empty,
	ErrorBanner,
	fmtNumber,
	fmtRelative,
	input,
	kr,
	Panel,
	Spinner,
} from "../../components/flows/ui";
import type {
	AttributeFetch,
	Candidate,
	CandidateList,
	CandidateStatus,
	ImportOutcome,
	ProductItem,
	RecordOutcome,
	SideAttributes,
} from "../../types/flows";

// The candidate queue exists so a human decision on a pair is made once and
// never re-asked. Dismissing is therefore as prominent as drafting, and the
// import path always previews before it writes: a typo in a pasted id list
// otherwise becomes a queue full of pairs nothing can ever draft.

const TABS: Array<{ id: CandidateStatus; label: string }> = [
	{ id: "new", label: "New" },
	{ id: "revise", label: "Revise" },
	{ id: "used", label: "Used" },
	{ id: "dismissed", label: "Dismissed" },
];

const MIN_SPECS = 3;

const fetchLine = (fetch: AttributeFetch | null): string | null => {
	if (!fetch) return null;
	if (fetch.status === "queued") return "attributes queued";
	if (fetch.status === "running") return "fetching attributes…";
	if (fetch.status === "failed")
		return `attribute fetch failed${fetch.error ? `: ${fetch.error}` : ""}`;
	return `fetched ${fmtRelative(fetch.finishedAt)}, +${fetch.added.length}`;
};

const ProductSide = ({
	product,
	attributes,
}: {
	product: ProductItem | null;
	attributes?: SideAttributes;
}) => {
	if (!product) {
		return (
			<div className="flex-1 text-[12px] text-slate-400">
				This product is no longer in the catalogue.
			</div>
		);
	}
	return (
		<div className="flex min-w-0 flex-1 items-start gap-2">
			{product.primaryImage ? (
				// Feed images hot-link from the advertiser and are never cached or
				// proxied (memory: merchant-image-affiliate-terms).
				<img
					src={product.primaryImage}
					alt=""
					className="h-12 w-12 shrink-0 rounded-lg border border-slate-100 object-contain"
				/>
			) : (
				<div className="h-12 w-12 shrink-0 rounded-lg border border-dashed border-slate-200" />
			)}
			<div className="min-w-0">
				<p
					className="truncate text-[12px] font-medium text-slate-700"
					title={product.name}
				>
					{product.name}
				</p>
				<p className="text-[11px] text-slate-500">
					{kr(product.lowestPrice)} · {product.merchantCount} merchant
					{product.merchantCount === 1 ? "" : "s"}
				</p>
				{attributes && (
					<p
						className={`text-[11px] ${attributes.specs < MIN_SPECS ? "text-amber-700" : "text-slate-500"}`}
						title={attributes.fetch?.added.join(", ") || undefined}
					>
						{attributes.specs} spec{attributes.specs === 1 ? "" : "s"}
						{fetchLine(attributes.fetch) && ` · ${fetchLine(attributes.fetch)}`}
					</p>
				)}
			</div>
		</div>
	);
};

const AddPairsDialog = ({
	onClose,
	onAdded,
}: {
	onClose: () => void;
	onAdded: () => void;
}) => {
	const [mode, setMode] = useState<"paste" | "pair">("paste");
	const [text, setText] = useState("");
	const [pairs, setPairs] = useState<Array<{ a: string; b: string }>>([
		{ a: "", b: "" },
	]);
	const [preview, setPreview] = useState<ImportOutcome | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const runImport = async (confirm: boolean) => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<ImportOutcome>(
				"/flows/comparisons/import",
				"POST",
				{
					text,
					confirm,
				},
			);
			if (confirm && result.applied) {
				onAdded();
				onClose();
				return;
			}
			setPreview(result);
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const addPairs = async () => {
		setBusy(true);
		setError(null);
		try {
			const parsed = pairs
				.map((pair) => [Number(pair.a), Number(pair.b)])
				.filter(
					([a, b]) =>
						Number.isInteger(a) && Number.isInteger(b) && a > 0 && b > 0,
				);
			if (parsed.length === 0) {
				setError("Enter at least one pair of product ids.");
				return;
			}
			const result = await sendJson<RecordOutcome & { error?: string }>(
				"/flows/comparisons",
				"POST",
				{ pairs: parsed },
			);
			if (result.error) setError(result.error);
			else {
				onAdded();
				onClose();
			}
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
			<div className="w-full max-w-2xl space-y-4 rounded-2xl bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between">
					<h3 className="text-[15px] font-semibold text-slate-800">
						Add pairs
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-slate-400 hover:bg-slate-100"
						aria-label="Close"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="flex gap-1 rounded-lg border border-slate-200 p-1">
					{(["paste", "pair"] as const).map((option) => (
						<button
							key={option}
							type="button"
							onClick={() => {
								setMode(option);
								setPreview(null);
							}}
							className={`flex-1 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
								mode === option
									? "bg-primary text-white"
									: "text-slate-500 hover:bg-slate-50"
							}`}
						>
							{option === "paste" ? "Paste a list" : "Enter ids"}
						</button>
					))}
				</div>

				{mode === "paste" ? (
					<div className="space-y-3">
						<label className="block space-y-1">
							<span className="block text-[12px] font-medium text-slate-600">
								One pair per line
							</span>
							<textarea
								className={`${input} min-h-36 font-mono text-[12px]`}
								placeholder={"12345 67890\n22222, 33333"}
								value={text}
								onChange={(event) => {
									setText(event.target.value);
									setPreview(null);
								}}
							/>
							<p className="text-[11px] text-slate-400">
								Nothing is written until you confirm the preview.
							</p>
						</label>

						{preview && (
							<div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12px]">
								<p className="font-medium text-slate-700">
									{preview.preview.length} usable pair
									{preview.preview.length === 1 ? "" : "s"} ·{" "}
									{preview.inserted + preview.alreadyKnown} would be recorded ·{" "}
									{preview.decided} already decided by a person ·{" "}
									{preview.invalid} unusable
								</p>
								{preview.decided > 0 && (
									<p className="text-slate-500">
										Pairs someone already dismissed or used are left exactly as
										they are — a decision is never re-asked.
									</p>
								)}
								<ul className="max-h-32 space-y-0.5 overflow-y-auto font-mono text-[11px] text-slate-600">
									{preview.preview.slice(0, 25).map((pair) => (
										<li key={`${pair.a}-${pair.b}`}>
											{pair.a} vs {pair.b}{" "}
											<span className="text-slate-400">({pair.status})</span>
										</li>
									))}
								</ul>
								{preview.badLines.length > 0 && (
									<div className="rounded-lg border border-warning/30 bg-warning/5 p-2">
										<p className="font-medium text-warning-dark">
											{preview.badLines.length} line
											{preview.badLines.length === 1 ? "" : "s"} could not be
											read
										</p>
										<ul className="mt-1 max-h-24 space-y-0.5 overflow-y-auto font-mono text-[11px] text-slate-600">
											{preview.badLines.map((line) => (
												<li key={line}>{line}</li>
											))}
										</ul>
									</div>
								)}
							</div>
						)}

						<ErrorBanner error={error} />

						<div className="flex justify-end gap-2">
							<button type="button" onClick={onClose} className={btn.ghost}>
								Cancel
							</button>
							{preview ? (
								<button
									type="button"
									disabled={busy || preview.preview.length === 0}
									onClick={() => runImport(true)}
									className={btn.primary}
								>
									Add {preview.preview.length} pair
									{preview.preview.length === 1 ? "" : "s"}
								</button>
							) : (
								<button
									type="button"
									disabled={busy || text.trim() === ""}
									onClick={() => runImport(false)}
									className={btn.primary}
								>
									Preview
								</button>
							)}
						</div>
					</div>
				) : (
					<div className="space-y-3">
						{pairs.map((pair, index) => (
							// Rows have no identity beyond their position.
							// biome-ignore lint/suspicious/noArrayIndexKey: no stable id exists
							<div key={index} className="flex items-center gap-2">
								<input
									className={input}
									placeholder="Product A id"
									value={pair.a}
									onChange={(event) => {
										const copy = [...pairs];
										copy[index] = { ...pair, a: event.target.value };
										setPairs(copy);
									}}
								/>
								<span className="text-[12px] text-slate-400">vs</span>
								<input
									className={input}
									placeholder="Product B id"
									value={pair.b}
									onChange={(event) => {
										const copy = [...pairs];
										copy[index] = { ...pair, b: event.target.value };
										setPairs(copy);
									}}
								/>
								<button
									type="button"
									onClick={() => setPairs(pairs.filter((_, i) => i !== index))}
									className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-danger"
									aria-label="Remove row"
								>
									<X className="h-4 w-4" />
								</button>
							</div>
						))}
						<button
							type="button"
							onClick={() => setPairs([...pairs, { a: "", b: "" }])}
							className="flex items-center gap-1 text-[12px] font-medium text-primary"
						>
							<Plus className="h-3.5 w-3.5" /> Another pair
						</button>

						<ErrorBanner error={error} />

						<div className="flex justify-end gap-2">
							<button type="button" onClick={onClose} className={btn.ghost}>
								Cancel
							</button>
							<button
								type="button"
								disabled={busy}
								onClick={addPairs}
								className={btn.primary}
							>
								Add pairs
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

const FlowsComparisons = () => {
	const navigate = useNavigate();
	const [search, setSearch] = useSearchParams();
	const status = (search.get("status") as CandidateStatus) ?? "new";

	const [data, setData] = useState<CandidateList | null>(null);
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [adding, setAdding] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [revising, setRevising] = useState<Candidate | null>(null);
	const [revisionNote, setRevisionNote] = useState("");
	const drafting = status === "new" || status === "revise";

	const load = useCallback(async () => {
		setError(null);
		try {
			setData(
				await fetchJson<CandidateList>(
					`/flows/comparisons?status=${status}&limit=50`,
				),
			);
			setSelected(new Set());
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [status]);

	useEffect(() => {
		load();
	}, [load]);

	const setStatusOf = async (
		candidate: Candidate,
		next: "new" | "dismissed" | "revise",
		note?: string,
	) => {
		setBusy(true);
		try {
			await sendJson(`/flows/comparisons/${candidate.id}/status`, "POST", {
				status: next,
				note,
			});
			setRevising(null);
			setRevisionNote("");
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const fetchAttributes = async (candidate: Candidate) => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{
				queued?: number[];
				skipped?: Array<{ productId: number; reason: string }>;
				error?: string;
			}>("/flows/comparisons/fetch-attributes", "POST", {
				ids: [candidate.id],
			});
			if (result.error) setError(result.error);
			else if (!result.queued?.length)
				setError(
					`Nothing queued: ${(result.skipped ?? []).map((s) => s.reason).join(", ") || "no products"}.`,
				);
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const createPages = async () => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{
				runId: number | null;
				error?: string;
				candidates?: number;
			}>("/flows/comparisons/create-pages", "POST", { ids: [...selected] });
			if (result.runId) navigate(`/flows/runs/${result.runId}`);
			// The server explains exactly why nothing happened — no published flow is
			// marked as the comparison drafting flow — so that text is the message.
			else setError(result.error ?? "The drafting run did not start.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	if (loading) return <Spinner />;

	const items = data?.items ?? [];
	const allSelected = items.length > 0 && selected.size === items.length;

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
					{TABS.map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => setSearch({ status: tab.id })}
							className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
								status === tab.id
									? "bg-primary text-white"
									: "text-slate-500 hover:bg-slate-50"
							}`}
						>
							{tab.label}
							{data && (
								<span className="ml-1.5 opacity-70">
									{data.counts[tab.id] ?? 0}
								</span>
							)}
						</button>
					))}
				</div>

				<div className="ml-auto flex items-center gap-2">
					<button
						type="button"
						onClick={() => setAdding(true)}
						className={btn.ghost}
					>
						<Plus className="h-4 w-4" /> Add pairs
					</button>
					{drafting && (
						<button
							type="button"
							disabled={busy || selected.size === 0}
							onClick={createPages}
							title={
								selected.size === 0
									? "Select the pairs you want written about"
									: undefined
							}
							className={btn.primary}
						>
							<FilePlus2 className="h-4 w-4" /> Create pages
							{selected.size > 0 && ` (${selected.size})`}
						</button>
					)}
				</div>
			</div>

			<ErrorBanner error={error} />

			<Panel
				title={`${TABS.find((t) => t.id === status)?.label} candidates`}
				hint={
					status === "new"
						? "select pairs and press Create pages — that starts one run of the drafting flow"
						: status === "revise"
							? "the next drafting run rewrites these pages first, or select them and press Create pages now"
							: undefined
				}
			>
				{items.length === 0 ? (
					<Empty>
						{status === "new"
							? "Nothing waiting. Candidates arrive from a Discovery flow's Pair up node, or from “Add pairs” above."
							: status === "revise"
								? "Nothing is waiting to be rewritten. Send a used pair here with Revise, or choose Revise at the review step."
								: status === "used"
									? "No pair has been written about yet."
									: "Nothing has been dismissed."}
					</Empty>
				) : (
					<div className="space-y-2">
						{drafting && (
							<label className="flex items-center gap-2 border-b border-slate-100 pb-2 text-[12px] text-slate-500">
								<input
									type="checkbox"
									checked={allSelected}
									onChange={(event) =>
										setSelected(
											event.target.checked
												? new Set(items.map((i) => i.id))
												: new Set(),
										)
									}
									className="h-4 w-4 rounded border-slate-300 text-primary"
								/>
								Select all on this page
							</label>
						)}

						{items.map((candidate) => (
							<div
								key={candidate.id}
								className="flex items-start gap-3 rounded-xl border border-slate-200 p-3"
							>
								{drafting && (
									<input
										type="checkbox"
										checked={selected.has(candidate.id)}
										onChange={(event) => {
											const next = new Set(selected);
											if (event.target.checked) next.add(candidate.id);
											else next.delete(candidate.id);
											setSelected(next);
										}}
										className="mt-1 h-4 w-4 rounded border-slate-300 text-primary"
									/>
								)}

								<div className="min-w-0 flex-1 space-y-2">
									<div className="flex items-start gap-3">
										<ProductSide
											product={candidate.a}
											attributes={candidate.attributes?.a}
										/>
										<span className="mt-4 shrink-0 text-[11px] font-medium text-slate-400">
											vs
										</span>
										<ProductSide
											product={candidate.b}
											attributes={candidate.attributes?.b}
										/>
									</div>

									<p className="text-[12px] text-slate-600">
										{candidate.suggestedTitle ?? "No suggested title"}
									</p>
									{candidate.status === "revise" && (
										<p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12px] text-amber-800">
											{candidate.revisionNote ??
												"Sent back without a note — the page is rewritten by the same rules."}
										</p>
									)}

									<div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
										<span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
											{candidate.source}
										</span>
										<span>demand {fmtNumber(candidate.demand)}</span>
										{candidate.matchScore !== null && (
											<span>· score {candidate.matchScore.toFixed(2)}</span>
										)}
										<span>· added {fmtRelative(candidate.createdAt)}</span>
										{candidate.decidedBy && (
											<span>· by {candidate.decidedBy}</span>
										)}
										{candidate.strapiEntryUrl && (
											<a
												href={candidate.strapiEntryUrl}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 font-medium text-primary"
											>
												Open in Strapi <ExternalLink className="h-3 w-3" />
											</a>
										)}
									</div>
								</div>

								<div className="flex shrink-0 flex-col items-end gap-1">
									<button
										type="button"
										disabled={busy}
										onClick={() => fetchAttributes(candidate)}
										title="Crawl both products' shop pages and fill the attributes they lack"
										className={btn.ghost}
									>
										<ListChecks className="h-4 w-4" /> Fetch attributes
									</button>
									{candidate.status === "dismissed" ? (
										<button
											type="button"
											disabled={busy}
											onClick={() => setStatusOf(candidate, "new")}
											className={btn.ghost}
										>
											<RotateCcw className="h-4 w-4" /> Restore
										</button>
									) : candidate.status === "new" ? (
										<button
											type="button"
											disabled={busy}
											onClick={() => setStatusOf(candidate, "dismissed")}
											className={btn.ghost}
										>
											<XCircle className="h-4 w-4" /> Dismiss
										</button>
									) : candidate.status === "used" ? (
										<button
											type="button"
											disabled={busy}
											onClick={() => setRevising(candidate)}
											title="Rewrite this page in the next drafting run"
											className={btn.ghost}
										>
											<PencilLine className="h-4 w-4" /> Revise
										</button>
									) : null}
								</div>
							</div>
						))}
					</div>
				)}
			</Panel>

			<p className="text-[11px] leading-relaxed text-slate-400">
				Drafting is never scheduled. A person picks the pairs, and{" "}
				<Link to="/flows" className="text-primary">
					the flow
				</Link>{" "}
				marked “comparison drafting” in its settings runs with exactly those
				ids.
			</p>

			{adding && (
				<AddPairsDialog onClose={() => setAdding(false)} onAdded={load} />
			)}

			{revising && (
				<ConfirmDialog
					title={`Revise ${revising.suggestedTitle ?? "this page"}`}
					confirmLabel="Send to Revise"
					busy={busy}
					onCancel={() => {
						setRevising(null);
						setRevisionNote("");
					}}
					onConfirm={() => setStatusOf(revising, "revise", revisionNote)}
					body={
						<>
							<p>
								The next drafting run rewrites this page and updates the draft
								it already has in Strapi, at the same address. Nothing is
								published until it is approved again.
							</p>
							<textarea
								className={`${input} min-h-24`}
								placeholder="What should change — the rewrite is given exactly this"
								value={revisionNote}
								onChange={(event) => setRevisionNote(event.target.value)}
							/>
						</>
					}
				/>
			)}
		</div>
	);
};

export default FlowsComparisons;
